/**
 * CustomerDedupeManager — Background scanner that surfaces possible duplicate
 * customers for review & merge. Same UX pattern as PriceScanManager:
 *   - Silent background scan on mount + every 5 min + on window focus.
 *   - "Review duplicates" floating button appears when clusters are found.
 *   - Dialog lets user pick a master, merge members, or mark them as different.
 *   - Respects a snooze window (localStorage) like the price scanner.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { formatPHP } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Users, RefreshCw, Merge, X, ChevronDown, Clock, Link2, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const SCAN_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const SKIP_KEY = 'agribooks_dedupe_scan_skip';

const SKIP_OPTIONS = [
  { label: '30 minutes', ms: 30 * 60 * 1000 },
  { label: '1 hour', ms: 60 * 60 * 1000 },
  { label: '4 hours', ms: 4 * 60 * 60 * 1000 },
  { label: 'Until tomorrow', ms: 12 * 60 * 60 * 1000 },
];

export default function CustomerDedupeManager() {
  const { user, currentBranch } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.is_super_admin;
  const [clusters, setClusters] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [skipUntil, setSkipUntil] = useState(null);
  const [showSkipMenu, setShowSkipMenu] = useState(false);

  // Per-cluster working state — master id + canonical name + submitting
  const [clusterState, setClusterState] = useState({}); // { clusterId: { masterId, canonicalName, submitting } }
  const timerRef = useRef(null);
  const skipMenuRef = useRef(null);

  // Skip for super-admin / no-tenant
  const skipForSuperAdmin = !!user && (user.is_super_admin || !user.organization_id);

  // Load skip state from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(SKIP_KEY);
    if (stored) {
      const until = parseInt(stored, 10);
      if (until > Date.now()) setSkipUntil(until);
      else localStorage.removeItem(SKIP_KEY);
    }
  }, []);

  // Click outside skip menu
  useEffect(() => {
    const h = (e) => { if (skipMenuRef.current && !skipMenuRef.current.contains(e.target)) setShowSkipMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const inSkipWindow = () => {
    const stored = localStorage.getItem(SKIP_KEY);
    if (!stored) return false;
    const until = parseInt(stored, 10);
    if (until > Date.now()) return true;
    localStorage.removeItem(SKIP_KEY);
    return false;
  };

  const runScan = useCallback(async (silent = true) => {
    if (!user || skipForSuperAdmin) return;
    setScanning(true);
    try {
      const params = {};
      // Admins/owners scan org-wide (no branch_id) to surface cross-branch
      // linked-customer candidates; everyone else is scoped to their branch.
      if (!isAdmin && currentBranch?.id) params.branch_id = currentBranch.id;
      const res = await api.get('/customers/-/duplicates', { params });
      const found = res.data?.clusters || [];
      setClusters(found);
      if (!silent) {
        toast.success(
          found.length === 0
            ? 'No duplicate customers found.'
            : `Found ${found.length} cluster${found.length === 1 ? '' : 's'} of possible duplicates.`
        );
      }
    } catch (e) {
      if (!silent) toast.error('Failed to scan for duplicates');
    }
    setScanning(false);
  }, [user, skipForSuperAdmin, currentBranch, isAdmin]);

  // Initial scan + polling + focus rescan
  useEffect(() => {
    if (!user || skipForSuperAdmin) return;
    runScan(true);
    const tick = () => { if (!inSkipWindow()) runScan(true); };
    timerRef.current = setInterval(tick, SCAN_INTERVAL_MS);
    window.addEventListener('focus', tick);
    // Expose a manual trigger (ImportPage fires this after a successful import)
    const handleCustomScan = () => runScan(true);
    window.addEventListener('customer-dedupe-rescan', handleCustomScan);
    return () => {
      clearInterval(timerRef.current);
      window.removeEventListener('focus', tick);
      window.removeEventListener('customer-dedupe-rescan', handleCustomScan);
    };
  }, [user, skipForSuperAdmin, currentBranch, runScan]);

  // Initialize cluster working state whenever the cluster list changes
  useEffect(() => {
    const next = {};
    clusters.forEach(c => {
      const existing = clusterState[c.id];
      next[c.id] = existing || {
        masterId: c.members[0]?.id || '',
        canonicalName: '',
        submitting: false,
      };
    });
    setClusterState(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusters]);

  const applySkip = (ms) => {
    const until = Date.now() + ms;
    localStorage.setItem(SKIP_KEY, String(until));
    setSkipUntil(until);
    setShowSkipMenu(false);
    setDialogOpen(false);
    toast.info(`Snoozed duplicate review for ${Math.round(ms / 60000)} minutes`);
  };

  const clearSkip = () => {
    localStorage.removeItem(SKIP_KEY);
    setSkipUntil(null);
    runScan(true);
  };

  // Drop a member from a cluster's local view; clear the cluster if it falls
  // below 2 and trigger a background rescan.
  const dropMember = (clusterId, memberId) => {
    setClusters(prev => prev.flatMap(c => {
      if (c.id !== clusterId) return [c];
      const members = c.members.filter(m => m.id !== memberId);
      if (members.length < 2) { runScan(true); return []; }
      return [{ ...c, members, member_count: members.length, cross_branch: new Set(members.map(m => m.branch_id)).size > 1 }];
    }));
  };

  // Per-member actions (parent → this member): link / merge / ignore.
  const actMember = async (cluster, parentId, member, action) => {
    const state = clusterState[cluster.id] || {};
    const sharedName = (state.canonicalName || '').trim();
    setClusterState(s => ({ ...s, [cluster.id]: { ...s[cluster.id], submitting: true } }));
    try {
      if (action === 'link') {
        const payload = { customer_ids: [parentId, member.id] };
        if (sharedName) payload.display_name = sharedName;
        await api.post('/customers/link', payload);
        toast.success(`Linked ${member.branch_name} account (view-only)`);
      } else if (action === 'merge') {
        const payload = { master_id: parentId, duplicate_ids: [member.id] };
        if (sharedName) payload.canonical_name = sharedName;
        await api.post('/customers/merge', payload);
        toast.success('Merged into the preferred record');
      } else { // ignore
        await api.post('/customers/mark-distinct', { customer_ids: [parentId, member.id] });
        toast.success('Marked as different — we won\u2019t flag this pair again');
      }
      dropMember(cluster.id, member.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setClusterState(s => ({ ...s, [cluster.id]: { ...s[cluster.id], submitting: false } }));
    }
  };

  if (!user || skipForSuperAdmin) return null;

  const clusterCount = clusters.length;
  const totalCustomers = clusters.reduce((acc, c) => acc + c.member_count, 0);

  // Floating pill — only when there's something to review
  const showPill = clusterCount > 0;

  return (
    <>
      {showPill && (
        // Bottom-LEFT so it never overlays the cart Checkout button on the
        // right rail of UnifiedSalesPage (it previously sat at bottom-right
        // and intercepted clicks on `[data-testid='checkout-btn']`, blocking
        // the testing agent's E2E flow). Also lifted slightly so it doesn't
        // sit on top of the global sidebar footer.
        <div className="fixed bottom-5 left-72 z-[60]" data-testid="dedupe-pill-wrapper">
          <Button
            data-testid="open-dedupe-dialog-btn"
            onClick={() => setDialogOpen(true)}
            className="bg-amber-500 hover:bg-amber-600 text-white shadow-lg animate-pulse-slow rounded-full h-11 px-4"
          >
            <Users size={15} className="mr-2" />
            {clusterCount} possible duplicate{clusterCount === 1 ? '' : 's'}
            <Badge className="ml-2 bg-white/20 text-white border-0 text-[10px]">
              {totalCustomers} customer{totalCustomers === 1 ? '' : 's'}
            </Badge>
          </Button>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="dedupe-dialog">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2 justify-between" style={{ fontFamily: 'Manrope' }}>
              <span className="flex items-center gap-2">
                <Users size={18} className="text-amber-600" />
                Possible Duplicate Customers
                {clusterCount > 0 && (
                  <Badge className="bg-amber-100 text-amber-700 border-amber-300 ml-2 text-[10px]">
                    {clusterCount} cluster{clusterCount === 1 ? '' : 's'}
                  </Badge>
                )}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm" variant="outline"
                  onClick={() => runScan(false)} disabled={scanning}
                  data-testid="dedupe-rescan-btn"
                >
                  <RefreshCw size={12} className={`mr-1 ${scanning ? 'animate-spin' : ''}`} />
                  Rescan
                </Button>
                <div className="relative" ref={skipMenuRef}>
                  <Button size="sm" variant="outline" onClick={() => setShowSkipMenu(v => !v)}>
                    <Clock size={12} className="mr-1" /> Snooze <ChevronDown size={10} className="ml-1" />
                  </Button>
                  {showSkipMenu && (
                    <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded shadow-lg z-50 min-w-[140px]">
                      {SKIP_OPTIONS.map(opt => (
                        <button key={opt.label} onClick={() => applySkip(opt.ms)}
                          className="block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-100">
                          {opt.label}
                        </button>
                      ))}
                      {skipUntil && (
                        <>
                          <div className="border-t border-slate-100" />
                          <button onClick={clearSkip}
                            className="block w-full text-left px-3 py-1.5 text-xs text-emerald-600 hover:bg-slate-100">
                            Clear snooze
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto mt-2 pr-1 space-y-5" data-testid="dedupe-cluster-list">
            {clusterCount === 0 && (
              <div className="text-center py-16 text-slate-400">
                <Users size={36} className="mx-auto mb-3 opacity-40" />
                <p className="text-sm font-medium">No possible duplicates right now.</p>
                <p className="text-xs mt-1">We'll keep scanning in the background.</p>
              </div>
            )}

            {clusters.map((cluster) => {
              const state = clusterState[cluster.id] || {};
              const masterId = state.masterId || cluster.members[0].id;
              return (
                <div key={cluster.id}
                  className={`border rounded-lg p-4 space-y-3 ${cluster.cross_branch ? 'border-teal-300 bg-teal-50/40' : 'border-amber-200 bg-amber-50/30'}`}
                  data-testid={`dedupe-cluster-${cluster.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                        {cluster.member_count} similar customer records
                        {cluster.cross_branch ? (
                          <Badge className="bg-teal-600 text-white border-0 text-[10px]" data-testid={`cluster-type-${cluster.id}`}>
                            <Building2 size={10} className="mr-0.5" /> CROSS-BRANCH
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-500 text-white border-0 text-[10px]" data-testid={`cluster-type-${cluster.id}`}>
                            <Users size={10} className="mr-0.5" /> SAME BRANCH
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {cluster.cross_branch ? (
                          <>Same customer in different branches? <strong>Link</strong> them (view-only — balances stay separate) or <strong>Ignore</strong>.</>
                        ) : (
                          <>Accidental duplicates in the same branch — pick the <strong>preferred (parent)</strong> record, then <strong>Merge</strong> the others into it or <strong>Ignore</strong>.</>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="text-xs text-slate-600 whitespace-nowrap">Shared display name (optional):</label>
                    <Input
                      data-testid={`canonical-name-${cluster.id}`}
                      value={state.canonicalName || ''}
                      onChange={(e) => setClusterState(s => ({
                        ...s,
                        [cluster.id]: { ...(s[cluster.id] || {}), canonicalName: e.target.value },
                      }))}
                      placeholder="Leave blank to keep the parent's name"
                      className="h-8 text-xs flex-1 min-w-[220px]"
                    />
                  </div>

                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs w-12">Parent</TableHead>
                        <TableHead className="text-xs">Name</TableHead>
                        <TableHead className="text-xs">Branch</TableHead>
                        <TableHead className="text-xs text-right">Balance</TableHead>
                        <TableHead className="text-xs text-right">Invoices</TableHead>
                        <TableHead className="text-xs text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cluster.members.map((m) => {
                        const parent = cluster.members.find(x => x.id === masterId) || cluster.members[0];
                        const isParent = masterId === m.id;
                        const sameBranch = parent && m.branch_id === parent.branch_id;
                        return (
                        <TableRow
                          key={m.id}
                          data-testid={`dedupe-member-${m.id}`}
                          className={isParent ? 'bg-emerald-50' : ''}
                        >
                          <TableCell>
                            <input
                              type="radio"
                              name={`master-${cluster.id}`}
                              data-testid={`pick-master-${m.id}`}
                              checked={isParent}
                              onChange={() => setClusterState(s => ({
                                ...s,
                                [cluster.id]: { ...(s[cluster.id] || {}), masterId: m.id },
                              }))}
                              className="w-4 h-4 accent-emerald-600 cursor-pointer"
                            />
                          </TableCell>
                          <TableCell className="text-sm font-medium">
                            {m.name}
                            {isParent && (
                              <Badge className="ml-2 bg-emerald-100 text-emerald-700 border-0 text-[10px]">parent</Badge>
                            )}
                            <span className="block text-[11px] text-slate-400">
                              {(m.phones?.length ? m.phones : (m.phone ? [m.phone] : [])).join(', ') || '—'}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs text-slate-500">{m.branch_name}</TableCell>
                          <TableCell className="text-right text-sm">
                            <span className={m.open_balance > 0 ? 'text-red-600 font-semibold' : 'text-slate-500'}>
                              {formatPHP(m.open_balance)}
                            </span>
                          </TableCell>
                          <TableCell className="text-right text-sm text-slate-600">{m.invoice_count}</TableCell>
                          <TableCell className="text-right">
                            {isParent ? (
                              <span className="text-[11px] text-slate-400">—</span>
                            ) : (
                              <div className="flex items-center justify-end gap-1">
                                {sameBranch ? (
                                  <Button size="sm" disabled={state.submitting}
                                    onClick={() => actMember(cluster, parent.id, m, 'merge')}
                                    className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] px-2"
                                    data-testid={`merge-member-${m.id}`}>
                                    <Merge size={12} className="mr-1" /> Merge
                                  </Button>
                                ) : isAdmin ? (
                                  <Button size="sm" disabled={state.submitting}
                                    onClick={() => actMember(cluster, parent.id, m, 'link')}
                                    className="h-7 bg-teal-600 hover:bg-teal-700 text-white text-[11px] px-2"
                                    data-testid={`link-member-${m.id}`}>
                                    <Link2 size={12} className="mr-1" /> Link
                                  </Button>
                                ) : null}
                                <Button size="sm" variant="outline" disabled={state.submitting}
                                  onClick={() => actMember(cluster, parent.id, m, 'ignore')}
                                  className="h-7 text-[11px] px-2"
                                  data-testid={`ignore-member-${m.id}`}>
                                  <X size={12} className="mr-1" /> Ignore
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );})}
                    </TableBody>
                  </Table>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
